import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useGetActionsQuery } from "app/services/missionStorageApiEnhanced";
import { getClient } from "app/services/missionStorageApi";
import { Action } from "app/services/types.generated";
import { MapMetadata } from "arcjs/common/navigation";
import { getStcmMapListBaseUrl } from "lib/consts/stcmMapList";
import { nav2TrinaryClassFromGrayscalePixel } from "lib/protocols/mapFilterYaml";
import {
    resolveActiveMissionId,
    resolveMapWaypointMissionId,
} from "lib/mission/activeMission";
import {
    firstLoadMapStep,
    logMissionMapPreviewSizes,
    missionPreviewImageUrl,
    missionPreviewOverlayGridCells,
    parseNavYamlGeometry,
    type MissionPreviewOverlayGridSource,
    type NavYamlGeometry,
} from "lib/mission/missionMapPreview";
import { stcmRelPathForMissionMapId } from "lib/mission/missionStorageMaps";
import useFleetArcId from "lib/utils/useFleetArcId";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import { useARC } from "providers/ARC/ARCProvider";
import { useMissionExecution } from "providers/navigation/MissionExecutionProvider";
import type { RootState } from "store";

export type MissionMapPreview = {
    imageUrl: string;
    mapMetadata: MapMetadata;
    label: string;
    filterYaml: NavYamlGeometry;
    /** Stored preview BMP/PNG pixel size (overlay may use filter grid cells instead). */
    previewImagePx: { width: number; height: number };
    overlayGridSource: MissionPreviewOverlayGridSource;
};

const MAP_ADAPTOR_KNOWN = 155;
const MAP_ADAPTOR_UNKNOWN = 210;
const MAP_ADAPTOR_OCCUPIED = 0;

const recolorLikeMapAdaptor = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
): void => {
    const image = ctx.getImageData(0, 0, width, height);
    const data = image.data;
    for (let i = 0; i < data.length; i += 4) {
        const klass = nav2TrinaryClassFromGrayscalePixel(data[i]);
        const value =
            klass === "unknown"
                ? MAP_ADAPTOR_UNKNOWN
                : klass === "free"
                  ? MAP_ADAPTOR_KNOWN
                  : MAP_ADAPTOR_OCCUPIED;
        data[i] = value;
        data[i + 1] = value;
        data[i + 2] = value;
        data[i + 3] = 255;
    }
    ctx.putImageData(image, 0, 0);
};

const MAP_FILTER_YAML_QUERY = `
  query MapPreviewFilterYaml($id: String!) {
    map(id: $id) {
      id
      name
      filterYaml
    }
  }
`;

/** North-up map_io PNGs: mirror both axes for this Leaflet CRS. */
const drawPreviewBothAxisFlip = (
    ctx: CanvasRenderingContext2D,
    bitmap: ImageBitmap,
    width: number,
    height: number,
): void => {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.translate(width, height);
    ctx.scale(-1, -1);
    ctx.drawImage(bitmap, 0, 0);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
};

const loadFlippedPreview = async (
    url: string,
): Promise<{
    objectUrl: string;
    width: number;
    height: number;
} | null> => {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    const bitmap = await createImageBitmap(blob);
    const width = bitmap.width;
    const height = bitmap.height;
    if (width <= 0 || height <= 0) {
        bitmap.close();
        return null;
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
        bitmap.close();
        return null;
    }
    drawPreviewBothAxisFlip(ctx, bitmap, width, height);
    bitmap.close();
    recolorLikeMapAdaptor(ctx, width, height);
    const flipped = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((next) => resolve(next), "image/png");
    });
    if (!flipped) return null;
    return {
        objectUrl: URL.createObjectURL(flipped),
        width,
        height,
    };
};

export const useMissionMapPreview = (): MissionMapPreview | null => {
    const { selectedARC } = useARC();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const { arcProxyHost } = useSelector((state: RootState) => state.arcProxy);
    const isCreating = useSelector((state: RootState) => state.missionCreation.isCreating);
    const editingMissionId = useSelector(
        (state: RootState) => state.missionCreation.currentMissionId,
    );
    const { executorState, isExecuting, currentMissionId } = useMissionExecution();

    const activeMissionId = resolveActiveMissionId({
        executorState,
        isExecuting,
        currentMissionId,
    });
    const source = activeMissionId ? "executing" : editingMissionId ? "editing" : null;
    const missionIdForMap = resolveMapWaypointMissionId({
        activeMissionId,
        editingMissionId,
    });
    const enabled = source === "editing" && !isCreating && Boolean(missionIdForMap);

    const { data: actionsData } = useGetActionsQuery(
        { missionId: missionIdForMap || "" },
        { skip: !enabled },
    );

    const [preview, setPreview] = useState<MissionMapPreview | null>(null);

    const stcmListBaseUrl = getStcmMapListBaseUrl(selectedARC?.host, isSecure, arcId);
    const loadMapKey = enabled
        ? JSON.stringify(
              firstLoadMapStep((actionsData?.actions || []) as Action[]) ?? null,
          )
        : "";

    useEffect(() => {
        if (!enabled || !stcmListBaseUrl || !loadMapKey || loadMapKey === "null") {
            setPreview(null);
            return;
        }

        const step = firstLoadMapStep((actionsData?.actions || []) as Action[]);
        if (!step) {
            setPreview(null);
            return;
        }

        let cancelled = false;
        setPreview(null);
        const apply = (next: MissionMapPreview | null) => {
            if (!cancelled) setPreview(next);
        };

        (async () => {
            try {
                const data = await getClient(arcProxyHost, arcId).request<{
                    map?: { id?: string; name?: string; filterYaml?: string | null } | null;
                }>(MAP_FILTER_YAML_QUERY, { id: step.mapId });
                const geo = parseNavYamlGeometry(data.map?.filterYaml);
                if (!geo) {
                    apply(null);
                    return;
                }
                const stcmRel = await stcmRelPathForMissionMapId(step.mapId, arcProxyHost, arcId);
                if (!stcmRel) {
                    apply(null);
                    return;
                }
                const imageUrl = missionPreviewImageUrl(
                    stcmListBaseUrl,
                    stcmRel,
                    step.variantBmpRelPath,
                );
                const flipped = await loadFlippedPreview(imageUrl);
                if (!flipped || cancelled) {
                    if (flipped) URL.revokeObjectURL(flipped.objectUrl);
                    apply(null);
                    return;
                }
                const label = (data.map?.name || "").trim() || stcmRel;
                const mapId = `mission-preview:${step.mapId}`;
                const previewImagePx = { width: flipped.width, height: flipped.height };
                const overlay = missionPreviewOverlayGridCells(geo, previewImagePx);
                logMissionMapPreviewSizes("preview loaded", {
                    mapId,
                    stcmRel,
                    variant: step.variantBmpRelPath ?? null,
                    previewImagePx,
                    filterYaml: geo,
                    overlayGridCells: { width: overlay.width, height: overlay.height },
                    overlayGridSource: overlay.source,
                });
                apply({
                    imageUrl: flipped.objectUrl,
                    label,
                    filterYaml: geo,
                    previewImagePx,
                    overlayGridSource: overlay.source,
                    mapMetadata: {
                        id: mapId,
                        name: label,
                        created_time: 0,
                        desc: "",
                        width: overlay.width,
                        height: overlay.height,
                        resolution: geo.resolution,
                        origin: { x: geo.originX, y: geo.originY },
                    },
                });
            } catch (error) {
                console.warn("Mission map preview unavailable:", error);
                apply(null);
            }
        })();

        return () => {
            cancelled = true;
            setPreview((current) => {
                if (current?.imageUrl.startsWith("blob:")) {
                    URL.revokeObjectURL(current.imageUrl);
                }
                return null;
            });
        };
        // Reload only when the Load Map step changes (not on every waypoint edit).
    }, [enabled, stcmListBaseUrl, loadMapKey, arcProxyHost, arcId]);

    return enabled ? preview : null;
};
